import React, { useEffect, useState } from 'react';
import {Table, Button, Tag, Input, Modal, DatePicker, Empty, message} from 'antd';
import {EditOutlined, FilePdfOutlined, FlagOutlined, RedoOutlined} from '@ant-design/icons';
import axios from 'axios';
import { OpenInternalOrderData } from '../../types/OpenInternalOrderData';
import './SystemRelease.css';
import LoadingModal from "../../components/loadingModal/LoadingModal";
import dayjs, {Dayjs} from "dayjs";
import {StatusUtils} from "../../utils/StatusUtils";
import {getConfig} from "../../config";
import SystemPlanningOrderCreation
    from "../internalOrders/systemPlanningOrder/systemPlanningOrderCreation/SystemPlanningOrderCreation";
import {TaskData} from "../../types/TaskData";
import {ViewType} from "../../types/ViewType";
import {useProjectContext} from "../../context/ProjectContext";
import {ColumnsType} from "antd/es/table";
import locale from "antd/es/date-picker/locale/de_DE";

const SystemRelease: React.FC = () => {
    const [data, setData] = useState<OpenInternalOrderData[]>([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [heliosSystemPlansUrl, setHeliosSystemPlansUrl] = useState('');
    const [releaseDate, setReleaseDate] = useState<Dayjs | null>(null);
    const [selectedPurchaseOrderNo, setSelectedPurchaseOrderNo] = useState<OpenInternalOrderData | null>(null);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(false);
    const [isKorrexModalVisible, setIsKorrexModalVisible] = useState(false);
    const [korrexData, setKorrexData] = useState<TaskData | null>(null);
    const { selectedProject, selectedManagerNo } = useProjectContext();
    const [modalLoading, setModalLoading] = useState(false);
    const { API_BASE_URL } = getConfig();

    useEffect(() => {
        if (selectedProject)    {
            fetchData(selectedProject);
        }
    }, [selectedProject]);

    const fetchData = (projectNo: string) => {
        setInitialLoading(true)
        axios
            .get<OpenInternalOrderData[]>(`${API_BASE_URL}/getPendingSystemPlanningApproval?auth=VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU&projectNo=${projectNo}&managerNo=${selectedManagerNo}`)
            .then(response => setData(response.data))
            .catch(error => console.error('Error fetching data:', error))
            .finally(() => {
                setInitialLoading(false); // Stop loading indicator regardless of success or error
            });

    };

    const handleInputChange = (value: string, setValue: (value: (((prevState: string) => string) | string)) => void) => {
        if (value === "h") {
            setValue("helios://");
        } else if (value.startsWith("helios://") || validateHeliosUrl(value)) {
            setValue(value);
        } else  {
            message.error("Bitte gebe eine gültige Helios-URL an");
        }
    };

    const validateHeliosUrl = (value: string): boolean => {
        return value.startsWith("helios://") && value.length > "helios://".length;
    };

    const fetchPDFAndOpen = async (purchaseOrderNo: string) => {
        setLoading(true); // Show loading spinner
        try {
            const response = await axios.get(
                `${API_BASE_URL}/getFullNetworkPlanOrderPDF?auth=VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU&purchaseOrderNo=${purchaseOrderNo}`,
                { responseType: 'arraybuffer' } // Ensure we receive binary data
            );
            const blob = new Blob([response.data], { type: "application/pdf" });
            const blobURL = URL.createObjectURL(blob);
            const pdfWindow = window.open(blobURL, '_blank');
            if (!pdfWindow) {
                message.error("Pop-up Blocker aktiv? Bitte erlaube Pop-ups für diese Seite.");
            }
        } catch (error) {
            message.error("Fehler beim Abrufen des PDFs.");
            console.error("Error fetching PDF:", error);
        } finally {
            setLoading(false);
        }
    };

    const columns: ColumnsType<OpenInternalOrderData> = [
        {
            title: 'Bestellung',
            dataIndex: 'purchaseOrderNo',
            key: 'purchaseOrderNo',
            render: (purchaseOrderNo: string) => (
                <Button
                    icon={<FilePdfOutlined />}
                    onClick={() => fetchPDFAndOpen(purchaseOrderNo)}
                    type="primary"
                    size="small"
                >
                    {purchaseOrderNo}
                </Button>
            ),
            width: 150
        },
        ...(selectedProject === "Alle"
            ? [
                {
                    title: 'Projekt',
                    dataIndex: 'projectNo',
                    key: 'projectNo',
                    sorter: (a: OpenInternalOrderData, b: OpenInternalOrderData) => (a.projectNo ?? '').localeCompare(b.projectNo ?? ''),
                },
            ]
            : []),
        {
            title: 'Projektleiter',
            dataIndex: 'projectManager',
            key: 'projectManager',
        },
        {
            title: 'Produkt',
            dataIndex: 'product',
            key: 'product',
            render: (text: string, record: OpenInternalOrderData) => (
                <>
                    {text}
                    {record.korrex && ` (Korrex)`}
                </>
            ),
        },
        {
            title: 'Vorgang',
            dataIndex: 'activityName',
            key: 'activityName',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: 'DONE' | 'READY' | 'NOT_READY' | 'PENDING') => {
                const color = StatusUtils.getColor(status);
                const translatedStatus = StatusUtils.getTranslatedStatus(status);
                return <Tag color={color}>{translatedStatus}</Tag>;
            },
        },
        {
            title: 'Aktion',
            key: 'actions',
            align: 'right', // Rechtsbündige Tabellenspalte
            render: (_: any, record: OpenInternalOrderData) => (
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                    <Button type="primary" icon={<FlagOutlined />} onClick={() => handleFinishOrder(record)} size="small">
                        Freigabe
                    </Button>
                    <Button danger icon={<EditOutlined />} onClick={() => handleCreateKorrex(record)} size="small">
                        Korrex
                    </Button>
                </div>
            ),
        },
    ];


    const handleFinishOrder = (record: OpenInternalOrderData) => {
        console.log("Finish order:", record);
        setSelectedPurchaseOrderNo(record);
        setIsModalVisible(true);
    };

    const fetchTaskData = async (networkPlanId: string) => {
        setModalLoading(true); // Show loading indicator
        try {
            const response = await axios.get<TaskData>(
                `${API_BASE_URL}/getSpecificNetworkTask`,
                {
                    params: {
                        auth: 'VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU',
                        projectNo: selectedProject,
                        type: 'systemPlanning',
                        networkPlanId,
                    },
                }
            );
            setKorrexData(response.data);
        } catch (error) {
            message.error('Fehler beim Laden der Systemplanungs-Daten.');
            console.error('Error fetching TaskData:', error);
        } finally {
            setModalLoading(false);
        }
    };

    const handleCreateKorrex = (record: OpenInternalOrderData) => {
        fetchTaskData(record.networkPlanId);
        setIsKorrexModalVisible(true);
    };

    const closeKorrexModal = () => {
        setIsKorrexModalVisible(false);
        setKorrexData(null); // Reset data when modal closes
    };

    const handleOk = async () => {
        console.log("Helios URL Systempläne entered:", heliosSystemPlansUrl);
        console.log("Datum der Freigabe:", releaseDate);
        if (!heliosSystemPlansUrl) return;
        setLoading(true);
        try {
            const response = await axios.get(
                `${API_BASE_URL}/bookSignedSystemplanNetworkOrder`, {
                    params: {
                        auth: 'VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU',
                        heliosURL: heliosSystemPlansUrl,
                        networkPlanId: selectedPurchaseOrderNo?.networkPlanId,
                        releaseDate: releaseDate
                            ? releaseDate.format('DD.MM.YYYY') // Day.js format method
                            : null,
                    }
                }
            );
            if (response.data) {
                message.success('Systemplanfreigabe erfolgreich gebucht!');
                setReleaseDate(null);
                setHeliosSystemPlansUrl('');
                setIsModalVisible(false);
                if (selectedProject)    {
                    fetchData(selectedProject);
                }
            }
        } catch (error) {
            message.error('Fehler beim Buchen der Systemplanfreigabe.');
            console.error('Error:', error);
        } finally {
            setLoading(false); // End loading
        }
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        setHeliosSystemPlansUrl('');
        setReleaseDate(null);
    };

    return (
        <div style={{ padding: '20px' }}>
            <LoadingModal
                open={initialLoading}
                text="Daten werden geladen..."
            />
            <LoadingModal
                open={loading}
                text="Systemplan wird gespeichert..."
            />
            <LoadingModal
                open={modalLoading}
                text="Korrex wird vorbereitet. Daten werden zurückgesetzt..."
            />
            <Table
                columns={columns}
                locale={{
                    emptyText: (
                        <Empty
                            description="Keine offenen Systemplanfreigaben vorhanden"
                        />
                    ),
                }}
                dataSource={data}
                rowKey={(record) => `${record.orderCardNo}-${record.networkPlanId}`}
                pagination={{ pageSize: 100 }}
                scroll={{ y: 700 }}
            />
            <Modal
                title="Erstellung Systemplanungs-Korrex"
                open={isKorrexModalVisible}
                onCancel={closeKorrexModal}
                footer={null}
                width={800}
            >
                {korrexData && (
                    <SystemPlanningOrderCreation
                        data={korrexData}
                        viewType={ViewType.KORREX}
                        onRefreshTable={() => {
                            closeKorrexModal();
                            if (selectedProject)    {
                                fetchData(selectedProject);
                            }
                        }}
                    />
                )}
            </Modal>
            <Modal
                title="Unterschriebenen Systemplan hochladen"
                open={isModalVisible}
                onOk={handleOk}
                onCancel={handleCancel}
                footer={[
                    <Button key="cancel" onClick={handleCancel} style={{ backgroundColor: 'red', color: 'white' }}>
                        Abbrechen
                    </Button>,
                    <Button disabled={!heliosSystemPlansUrl || !releaseDate}  key="submit" type="primary" onClick={handleOk}>
                        Speichern
                    </Button>,
                ]}
            >
                <Input
                    placeholder="Helios-URL"
                    value={heliosSystemPlansUrl}
                    onChange={(e) => handleInputChange(e.target.value, setHeliosSystemPlansUrl)}
                    style={{ marginBottom: '15px' }}
                />
                <DatePicker
                    locale={locale}
                    style={{ width: '100%' }}
                    placeholder="Datum der Freigabe"
                    value={releaseDate}
                    onChange={(date) => setReleaseDate(date)}
                    format="DD.MM.YYYY"
                    disabledDate={(current) => {
                        return current && current.isAfter(dayjs().endOf('day'));
                    }}
                />
            </Modal>
        </div>
    );
};

export default SystemRelease;
